import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { USER_CONTEXT_TOKEN_KEY } from '@deighton/shared-ui/core';
import OktaAuth, { AccessToken, Token, TokenResponse, UserClaims } from '@okta/okta-auth-js';
import { firstValueFrom } from 'rxjs';
import { DEIGHTON_PLATFORM_API_BASE_URL } from '@deighton/shared-ui/services';
import { environment } from '../environments/environment';
import { SessionExpiredService } from './session-expired.service';

/** Shape of the backend code-exchange response. */
interface AuthCodeExchangeResponse {
  /** The Okta authorization code. */
  code: string;
  /** PKCE code verifier paired with the original code challenge. */
  codeVerifier: string;
  /** Redirect URI used during the original /authorize request. */
  redirectUri: string;
  /** Tenant-specific Okta configuration returned by the backend. */
  oktaConfig: {
    /** Tenant's Okta issuer URL. */
    issuerUrl: string;
    /** Tenant's Okta client ID. */
    clientId: string;
    /** DTIMS tenant identifier. */
    tenantId: string;
    /** DTIMS environment identifier. */
    environmentId: string;
  };
  /** Single-use nonce. Must be returned to POST /api/auth/session to prevent replays. */
  sessionNonce: string;
}

/** Shape of the backend session response. */
interface AuthSessionResponse {
  /** Signed user context JWT used as the X-Deighton-User-Context header. */
  userContextToken?: string;
  /** Redirect URL suggested by the backend (optional, not used in standalone mode). */
  redirectUrl?: string;
}

/** localStorage key for the Okta access token in standalone licensing mode. */
export const LICENSING_ACCESS_TOKEN_KEY: string = 'dtims_licensing_access_token';

/** localStorage key for the current user's identity (username + email) used for UI self-checks. */
export const LICENSING_USER_IDENTITY_KEY: string = 'dtims_licensing_user_identity';

/** Identity of the currently signed-in user, derived from the Okta ID token claims. */
export interface LicensingUserIdentity {
  /** Preferred username (falls back to email / subject). */
  userName: string;
  /** Email claim, when present. */
  email: string;
}

/** Default OktaAuth TokenManager storage key when none is configured in the environment. */
const DEFAULT_OKTA_STORAGE_KEY: string = 'okta-token-storage-licensing';

/**
 * Service that manages the backend-mediated OAuth 2.0 Authorization Code with PKCE flow
 * for the standalone licensing application.
 *
 * Replicates the shell-app's AuthService + AuthCodeExchangeService pattern:
 * 1. User is redirected to the backend `/auth` page for tenant/environment selection.
 * 2. Backend initiates the Okta PKCE flow and redirects back with a relay token.
 * 3. This service exchanges the relay token for the authorization code and PKCE verifier.
 * 4. It performs the native Okta token exchange and stores the tokens.
 * 5. It establishes a DTIMS session on the backend and stores the user context token.
 *
 * TODO(de-dup): The relay-token exchange, native Okta token exchange, and session
 * establishment in this service are near-duplicates of
 * `apps/shell-app/src/services/auth-code-exchange.service.ts`. The only structural
 * difference is that shell-app keeps a separate bootstrap `OKTA_AUTH` instance
 * (via `OktaAuthModule.forRoot`) while licensing creates its single OktaAuth
 * dynamically here, because licensing has no static tenant Okta config in
 * `environment.okta`. When both apps converge on a single token-storage strategy,
 * lift the shared logic into `@deighton/shared-ui/auth`.
 */
@Injectable({ providedIn: 'root' })
export class LicensingAuthService {
  /** Context identification for SSR compatibility. */
  private readonly platformId: object = inject(PLATFORM_ID);

  /** HttpClient routes requests through the app's auth interceptor pipeline. */
  private readonly http: HttpClient = inject(HttpClient);

  /** Clears session-expired UI state after a successful sign-in. */
  private readonly sessionExpiredService: SessionExpiredService = inject(SessionExpiredService);

  /** Whether the current platform is a browser. */
  private readonly isBrowser: boolean = isPlatformBrowser(this.platformId);

  /** Platform API base: this app's env when standalone, the shell's region when embedded. */
  private readonly apiBaseUrl: string = inject(DEIGHTON_PLATFORM_API_BASE_URL);

  /**
   * The single OktaAuth instance for the current session. Held so it can be torn down
   * (auto-renew services stopped, listeners removed) before a new one is created — this
   * prevents leaking instances and orphaned renewal timers across repeated callbacks.
   */
  private authClient: OktaAuth | null = null;

  /** Reference to the `renewed` listener so it can be detached during teardown. */
  private renewedListener: ((key: string, token: Token) => void) | null = null;

  /**
   * Returns whether the user has an established DTIMS session.
   * Checks for the presence of the user context token in localStorage.
   * @returns True if the user context token is stored.
   */
  public isAuthenticated(): boolean {
    if (!this.isBrowser) {
      return false;
    }
    return !!localStorage.getItem(USER_CONTEXT_TOKEN_KEY);
  }

  /**
   * Redirects the browser to the backend authentication page for tenant/environment
   * selection and Okta PKCE login. The backend will redirect to this app's
   * /auth-callback route with a relay token after authentication completes.
   */
  public redirectToLogin(): void {
    if (!this.isBrowser) {
      return;
    }
    const returnUrl: string = `${window.location.origin}/auth-callback`;
    const loginUrl: string = `${this.apiBaseUrl}/auth?return_url=${encodeURIComponent(returnUrl)}`;
    window.location.href = loginUrl;
  }

  /**
   * Clears all stored tokens and tears down the OktaAuth instance, ending the session.
   */
  public signOut(): void {
    void this.teardownAuthClient();

    if (!this.isBrowser) {
      return;
    }
    localStorage.removeItem(LICENSING_ACCESS_TOKEN_KEY);
    localStorage.removeItem(USER_CONTEXT_TOKEN_KEY);
    localStorage.removeItem(LICENSING_USER_IDENTITY_KEY);
    const storageKey: string = environment.okta?.oktaStorageKey ?? DEFAULT_OKTA_STORAGE_KEY;
    localStorage.removeItem(storageKey);
  }

  /**
   * Returns the currently signed-in user's identity (username + email), or null when
   * unavailable. Identity is persisted after a successful standalone login; it may also
   * be derived from the shared user-context JWT when that token is present (e.g. embedded
   * shell sessions that never ran the licensing auth callback). For UI hints only — backend
   * enforcement remains authoritative when identity cannot be resolved.
   * @returns The stored or derived user identity, or null.
   */
  public getCurrentUserIdentity(): LicensingUserIdentity | null {
    if (!this.isBrowser) {
      return null;
    }
    const raw: string | null = localStorage.getItem(LICENSING_USER_IDENTITY_KEY);
    if (raw) {
      try {
        const parsed: LicensingUserIdentity = JSON.parse(raw) as LicensingUserIdentity;
        if (parsed && (parsed.email || parsed.userName)) {
          return this.normalizeIdentity(parsed);
        }
      } catch {
        // Fall through to deriving the identity from the user-context token.
      }
    }
    // Fallback for sessions established before the identity was persisted (or if it was
    // cleared): derive the identity from the signed user-context JWT, which is always
    // present while authenticated.
    return this.deriveIdentityFromContextToken();
  }

  /**
   * Derives the current user's identity from the stored user-context JWT by reading common
   * identity claims. Used as a fallback for {@link getCurrentUserIdentity}. Returns null when
   * no token is present or no recognisable identity claim is found.
   * @returns The derived identity, or null.
   */
  private deriveIdentityFromContextToken(): LicensingUserIdentity | null {
    const claims: Record<string, unknown> | null = this.decodeJwtPayload(
      localStorage.getItem(USER_CONTEXT_TOKEN_KEY)
    );
    if (!claims) {
      return null;
    }

    const pickClaim: (keys: ReadonlyArray<string>) => string = (keys: ReadonlyArray<string>): string => {
      for (const key of keys) {
        const value: unknown = claims[key];
        if (typeof value === 'string' && value.length > 0) {
          return value;
        }
        if (Array.isArray(value)) {
          const firstString: string | undefined = value.find(
            (item: unknown): item is string => typeof item === 'string' && item.length > 0
          );
          if (firstString) {
            return firstString;
          }
        }
      }
      return '';
    };

    const email: string = pickClaim(['email', 'emails', 'upn', 'unique_name']);
    const userName: string = pickClaim([
      'preferred_username',
      'username',
      'userName',
      'unique_name',
      'name',
      'sub',
    ]);

    if (!email && !userName) {
      return null;
    }
    return this.normalizeIdentity({ userName, email });
  }

  /**
   * Safely decodes the payload of a JWT without verifying its signature.
   * For UI hints only — never use the decoded claims for security decisions.
   * @param token - The JWT string, or null.
   * @returns The decoded payload claims, or null when the token cannot be decoded.
   */
  private decodeJwtPayload(token: string | null): Record<string, unknown> | null {
    if (!token) {
      return null;
    }
    const segments: Array<string> = token.split('.');
    if (segments.length < 2) {
      return null;
    }
    try {
      const base64: string = segments[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded: string = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const json: string = atob(padded);
      return JSON.parse(json) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  /**
   * Normalizes identity fields for consistent UI comparisons (case-insensitive email/username).
   * @param identity - Raw identity values from token claims or storage.
   * @returns Lowercased identity suitable for persistence and lookup.
   */
  private normalizeIdentity(identity: LicensingUserIdentity): LicensingUserIdentity {
    return {
      userName: (identity.userName || '').toLowerCase(),
      email: (identity.email || '').toLowerCase(),
    };
  }

  /**
   * Persists the current user's identity for later UI self-checks (e.g. preventing a user
   * from revoking their own governance roles).
   * @param identity - The identity derived from the Okta ID token claims.
   */
  private storeUserIdentity(identity: LicensingUserIdentity): void {
    if (!this.isBrowser) {
      return;
    }
    localStorage.setItem(LICENSING_USER_IDENTITY_KEY, JSON.stringify(this.normalizeIdentity(identity)));
  }

  /**
   * Handles the auth callback by orchestrating the relay token → code exchange →
   * native token exchange → token storage → session establishment flow.
   *
   * @throws If any step of the exchange fails.
   */
  public async handleAuthCallback(): Promise<void> {
    const relayToken: string | null = this.extractRelayToken();
    const exchangeData: AuthCodeExchangeResponse = await this.exchangeRelayTokenForCode(relayToken);
    const tokenResponse: TokenResponse = await this.exchangeCodeForTokens(exchangeData);
    await this.storeTokensAndSetupRenewal(tokenResponse);
    await this.establishSession(exchangeData, tokenResponse);
  }

  /**
   * Reads `relay_token` from the URL query parameters and strips it from history.
   * @returns The relay token, or null when unavailable.
   */
  private extractRelayToken(): string | null {
    if (!this.isBrowser) {
      return null;
    }
    const urlParams: URLSearchParams = new URLSearchParams(window.location.search);
    const relayToken: string | null = urlParams.get('relay_token');
    this.stripUrlParameter('relay_token');
    return relayToken;
  }

  /**
   * Exchanges the relay token for the authorization code, PKCE verifier, Okta config, and nonce.
   * @param relayToken - The relay token returned by the backend auth page.
   * @returns The backend code-exchange payload.
   * @throws If the code exchange request fails.
   */
  private async exchangeRelayTokenForCode(relayToken: string | null): Promise<AuthCodeExchangeResponse> {
    try {
      return await firstValueFrom(
        this.http.post<AuthCodeExchangeResponse>(
          `${this.apiBaseUrl}/api/auth/code-exchange`,
          { relayToken },
          { withCredentials: true }
        )
      );
    } catch (error: unknown) {
      throw this.toError('Code exchange', error);
    }
  }

  /**
   * Creates a fresh tenant-specific OktaAuth instance (tearing down any prior one)
   * and exchanges the authorization code for tokens.
   * @param exchangeData - The backend code-exchange payload.
   * @returns The Okta token response.
   */
  private async exchangeCodeForTokens(exchangeData: AuthCodeExchangeResponse): Promise<TokenResponse> {
    await this.teardownAuthClient();

    const storageKey: string = environment.okta?.oktaStorageKey ?? DEFAULT_OKTA_STORAGE_KEY;
    this.authClient = new OktaAuth({
      issuer: exchangeData.oktaConfig.issuerUrl,
      clientId: exchangeData.oktaConfig.clientId,
      redirectUri: exchangeData.redirectUri,
      pkce: true,
      scopes: ['openid', 'profile', 'email', 'offline_access'],
      tokenManager: {
        storage: 'localStorage',
        storageKey,
        autoRenew: true,
      },
      services: { autoRenew: true },
    });

    return this.authClient.token.exchangeCodeForTokens({
      authorizationCode: exchangeData.code,
      codeVerifier: exchangeData.codeVerifier,
    });
  }

  /**
   * Adds the exchanged tokens to the SDK TokenManager, starts auto-renewal, persists the
   * raw access token for the HTTP interceptor, and wires the renewal listener to keep it in sync.
   * @param tokenResponse - The Okta token response.
   * @throws If the OktaAuth instance has not been initialized.
   */
  private async storeTokensAndSetupRenewal(tokenResponse: TokenResponse): Promise<void> {
    if (!this.authClient) {
      throw new Error('OktaAuth client is not initialized.');
    }

    if (tokenResponse.tokens.accessToken) {
      this.authClient.tokenManager.add('accessToken', tokenResponse.tokens.accessToken);
    }
    if (tokenResponse.tokens.idToken) {
      this.authClient.tokenManager.add('idToken', tokenResponse.tokens.idToken);
    }
    if (tokenResponse.tokens.refreshToken) {
      this.authClient.tokenManager.add('refreshToken', tokenResponse.tokens.refreshToken);
    }

    await this.authClient.start();

    const rawAccessToken: string | undefined = tokenResponse.tokens.accessToken?.accessToken;
    if (rawAccessToken && this.isBrowser) {
      localStorage.setItem(LICENSING_ACCESS_TOKEN_KEY, rawAccessToken);
    }

    this.sessionExpiredService.clear();

    // Keep the interceptor's token in sync with auto-renewal.
    this.renewedListener = (key: string, newToken: Token): void => {
      if (key === 'accessToken' && this.isBrowser) {
        const accessToken: AccessToken = newToken as AccessToken;
        if (accessToken.accessToken) {
          localStorage.setItem(LICENSING_ACCESS_TOKEN_KEY, accessToken.accessToken);
        }
      }
    };
    this.authClient.tokenManager.on('renewed', this.renewedListener);
  }

  /**
   * Establishes the DTIMS session on the backend using the ID token claims and nonce,
   * then stores the returned user context token.
   * @param exchangeData - The backend code-exchange payload.
   * @param tokenResponse - The Okta token response.
   * @throws If the session establishment request fails.
   */
  private async establishSession(
    exchangeData: AuthCodeExchangeResponse,
    tokenResponse: TokenResponse
  ): Promise<void> {
    const idClaims: UserClaims | undefined = tokenResponse.tokens.idToken?.claims;
    const username: string = idClaims?.preferred_username ?? idClaims?.email ?? idClaims?.sub ?? '';
    const idpUserId: string = idClaims?.sub ?? '';

    let sessionData: AuthSessionResponse;
    try {
      sessionData = await firstValueFrom(
        this.http.post<AuthSessionResponse>(
          `${this.apiBaseUrl}/api/auth/session`,
          {
            tenantId: exchangeData.oktaConfig.tenantId,
            environmentId: exchangeData.oktaConfig.environmentId,
            username,
            idpUserId,
            sessionNonce: exchangeData.sessionNonce,
          },
          { withCredentials: true }
        )
      );
    } catch (error: unknown) {
      throw this.toError('Session establishment', error);
    }

    if (sessionData.userContextToken && this.isBrowser) {
      localStorage.setItem(USER_CONTEXT_TOKEN_KEY, sessionData.userContextToken);
    }

    this.storeUserIdentity({ userName: username, email: idClaims?.email ?? '' });
  }

  /**
   * Stops auto-renew services and detaches listeners on the current OktaAuth instance,
   * then releases it. Safe to call when no instance exists.
   */
  private async teardownAuthClient(): Promise<void> {
    if (!this.authClient) {
      return;
    }
    if (this.renewedListener) {
      this.authClient.tokenManager.off('renewed', this.renewedListener);
      this.renewedListener = null;
    }
    await this.authClient.stop();
    this.authClient = null;
  }

  /**
   * Normalizes an HTTP/unknown error into a descriptive Error for the callback UI.
   * @param context - Human-readable step name used as the message prefix.
   * @param error - The caught error.
   * @returns A descriptive Error instance.
   */
  private toError(context: string, error: unknown): Error {
    if (error instanceof HttpErrorResponse) {
      const detail: string = typeof error.error === 'string' ? error.error : (error.message ?? '');
      return new Error(`${context} failed (HTTP ${error.status}): ${detail}`);
    }
    return error instanceof Error ? error : new Error(`${context} failed: ${String(error)}`);
  }

  /**
   * Removes a query parameter from the browser's current URL without causing a page reload.
   * @param paramName - The query parameter to remove.
   */
  private stripUrlParameter(paramName: string): void {
    if (!this.isBrowser) {
      return;
    }
    const url: URL = new URL(window.location.href);
    if (url.searchParams.has(paramName)) {
      url.searchParams.delete(paramName);
      window.history.replaceState({}, '', url.toString());
    }
  }
}
